import { useEffect, useRef, useLayoutEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { LockBadge } from '@/components/ui'

type ModelType = 'basic' | 'standard' | 'premium'

interface DurationSelectorProps {
  model: ModelType
  value: string
  onChange: (value: string) => void
  /** Whether user has Creator plan access (for 10s/12s durations) */
  hasCreatorAccess?: boolean
  /** Called when a locked duration is clicked */
  onLockedClick?: (duration: string) => void
  /** Disable the selector */
  disabled?: boolean
}

const durationsByModel: Record<ModelType, string[]> = {
  basic: ['5'],
  standard: ['5', '10'],
  premium: ['5', '10'],
}

const creatorDurations = ['10']

export function DurationSelector({ 
  model, 
  value, 
  onChange, 
  hasCreatorAccess = true,
  onLockedClick,
  disabled = false,
}: DurationSelectorProps) {
  const options = durationsByModel[model]
  const containerRef = useRef<HTMLDivElement>(null)
  const optionRefs = useRef<Map<string, HTMLButtonElement>>(new Map())
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 })

  // Reset to first available duration when model changes
  useEffect(() => {
    if (!options.includes(value)) {
      onChange(options[0])
    }
  }, [model, value, onChange, options])

  // Update indicator position
  useLayoutEffect(() => {
    const rafId = requestAnimationFrame(() => {
      const activeEl = optionRefs.current.get(value)
      const container = containerRef.current
      if (activeEl && container) {
        const containerRect = container.getBoundingClientRect()
        const activeRect = activeEl.getBoundingClientRect()
        setIndicatorStyle({
          left: activeRect.left - containerRect.left,
          width: activeRect.width,
        })
      }
    })
    return () => cancelAnimationFrame(rafId)
  }, [value, options])

  const handleClick = (opt: string) => {
    if (disabled) return
    const isLocked = creatorDurations.includes(opt) && !hasCreatorAccess
    if (isLocked && onLockedClick) {
      onLockedClick(opt)
    } else if (!isLocked) {
      onChange(opt)
    }
  }

  return (
    <div className="flex flex-col rounded-xl bg-white/[0.03] px-3 py-2 md:px-4 md:py-3 h-full">
      <span className="text-xs text-[#666]">Duration</span>
      <div 
        ref={containerRef}
        className="relative flex gap-1 mt-auto"
      >
        {/* Sliding indicator */}
        <div
          className="absolute top-0 bottom-0 rounded-md bg-white/[0.08] border border-white/[0.1] transition-all duration-300 ease-out pointer-events-none"
          style={{
            left: indicatorStyle.left,
            width: indicatorStyle.width,
          }}
        />
        {options.map((opt) => {
          const isLocked = creatorDurations.includes(opt) && !hasCreatorAccess
          return (
            <button
              key={opt}
              type="button"
              ref={(el) => {
                if (el) optionRefs.current.set(opt, el)
                else optionRefs.current.delete(opt)
              }}
              onClick={() => handleClick(opt)}
              className={cn(
                "relative z-10 flex-1 flex items-center justify-center gap-1.5 px-3 py-1 rounded-md text-sm font-medium transition-colors duration-200 whitespace-nowrap",
                value === opt
                  ? "text-white"
                  : "text-[#666] hover:text-[#999]",
                isLocked && "opacity-70"
              )}
            >
              {opt}s
              {isLocked && <LockBadge variant="violet" className="size-3.5" />}
            </button>
          )
        })}
      </div>
    </div>
  )
}
